import type { ProFormColumnsType } from '@ant-design/pro-components';
import { Card } from 'antd';
import dayjs from 'dayjs';
import { DeleteIcon } from '@components/common/Icons/DeleteIcon';
import type { BaseFormSchemaProps } from '@pages/BtlApplicationFormPage/types';

export type DependantsFormSchemaProps = BaseFormSchemaProps & {
  /**
   * Whether the case already has dependants stored server-side. Needed at schema-build time so the
   * list can be omitted entirely rather than rendered empty — see `hideInForm` below.
   */
  hasStoredDependants?: boolean;
};

export function dependantsFormSchema(props: DependantsFormSchemaProps): ProFormColumnsType[] {
  const { formatMessage } = props;
  const requiredMessage = formatMessage({ id: 'common.error.requiredField' });

  // Same rule as occupiers: the only case with nothing to show is a read-only form holding no
  // dependants, where the section would otherwise leave an empty gap above the next heading.
  const isSectionHidden = !props.isFormEditable && !props.hasStoredDependants;

  return [
    {
      hideInForm: isSectionHidden,
      renderFormItem: () => (
        <div className='-mb-4'>
          <h3 className='mb-1'>{formatMessage({ id: 'ooCase.expenses.dependants.header' })}</h3>
          <p className='text-gray-500'>{formatMessage({ id: 'ooCase.expenses.dependants.hint' })}</p>
        </div>
      ),
    },
    {
      dataIndex: ['dependants'],
      valueType: 'formList',
      // Omitting the column removes the empty Form.Item wrapper a rowless list would otherwise
      // leave behind. Safe only because this is exactly the case where nothing is stored:
      // `mapOoExpensesFormValuesToHousehold` always sends `dependants`, and an unregistered field
      // would submit `[]` and delete whatever the server holds — taking the derived counts with it.
      hideInForm: isSectionHidden,
      fieldProps: {
        copyIconProps: false,
        creatorButtonProps: props.isFormEditable && {
          position: 'bottom',
          creatorButtonText: formatMessage({ id: 'ooCase.expenses.dependants.addDependant.label' }),
        },
        itemRender: ({ listDom }: any, { index, operation }: any) => (
          <Card
            title={`${formatMessage({ id: 'ooCase.expenses.dependants.dependant.label' })} ${index + 1}`}
            className={'bg-white/60 border-gray-300 border-dashed'}
            extra={
              operation.remove && props.isFormEditable ? (
                <a onClick={() => operation.remove(index)}>
                  <DeleteIcon
                    htmlColor={'#DC2626'}
                    titleAccess={formatMessage({ id: 'ooCase.expenses.dependants.removeDependant.label' })}
                  />
                </a>
              ) : null
            }
            style={{ marginBottom: 16 }}
          >
            {listDom}
          </Card>
        ),
      },
      columns: [
        {
          dataIndex: 'id',
          valueType: 'text',
          formItemProps: { hidden: true, noStyle: true },
          fieldProps: { hidden: true },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.dependants.firstName.label' }),
          dataIndex: 'firstName',
          valueType: 'text',
          width: 300,
          formItemProps: {
            hasFeedback: true,
            rules: [{ required: true, message: requiredMessage }],
          },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.dependants.lastName.label' }),
          dataIndex: 'lastName',
          valueType: 'text',
          width: 300,
          formItemProps: {
            hasFeedback: true,
            rules: [{ required: true, message: requiredMessage }],
          },
        },
        {
          title: formatMessage({ id: 'ooCase.expenses.dependants.dateOfBirth.label' }),
          dataIndex: 'dateOfBirth',
          valueType: 'date',
          width: 300,
          fieldProps: {
            format: {
              format: 'DD/MM/YYYY',
              type: 'mask',
            },
            placeholder: formatMessage({ id: 'common.dateMask' }),
          },
          formItemProps: {
            hasFeedback: true,
            getValueFromEvent: (date: unknown) => (date ? dayjs(date as never).format('YYYY-MM-DD') : null),
            rules: [
              { required: true, message: requiredMessage },
              {
                validator: async (_: unknown, value: string | null) => {
                  if (value && dayjs(value).isAfter(dayjs(), 'day')) {
                    return Promise.reject(
                      new Error(formatMessage({ id: 'ooCase.expenses.dependants.dateOfBirth.futureError' })),
                    );
                  }
                },
              },
            ],
          },
        },
      ],
    },
  ];
}
